/**
 * TOSS Web Application - Client portal layout
 *
 * The chrome of the external customer portal.
 *
 * ```
 * Desktop (>= 1024) / tablet (768–1023, sidebar collapsed to icons)
 * ┌──────────────┬────────────────────────────────────┐
 * │ logo         │ ClientHeader (section / company)   │
 * │ company      ├────────────────────────────────────┤
 * │ ClientSidebar│  <main>  (toolbar, then content)   │
 * └──────────────┴────────────────────────────────────┘
 *
 * Phone (<= 767)
 * ┌──────────────────────────────────────────────────┐
 * │                 ClientHeader                     │
 * │                 <main>                           │
 * │                 ClientBottomNav (fixed)          │
 * └──────────────────────────────────────────────────┘
 * ```
 *
 * `ClientHeader` renders at EVERY width again since 2026-08-24. It was phone-only between
 * 2026-08-21 and then (decision Р33): above 767 it printed the section name that the page printed
 * again. The owner reversed Р33 in favour of the wireframes; the duplicate is prevented at the
 * page end instead — `ClientPageHeader` no longer takes a title. The legal-entity switcher stays
 * in the sidebar, where Р33 moved it: on a computer the bar names the company in plain type,
 * because the switcher renders nothing for a single-company account.
 *
 * Written fresh rather than as a branch inside `AppLayout` (559 lines), which
 * unconditionally opens a messenger SignalR connection and renders `CommandPalette`,
 * `CalculatorPanel`, `MessengerFab` and a slide-out `MobileDrawer`. The portal's phone
 * pattern is a bottom bar, not a drawer — two incompatible mobile patterns in one file
 * is exactly what decision Р5/Р6 avoids. `MessengerFab` simply is not rendered; no edit
 * to it was needed anywhere.
 *
 * Connection state: the portal reports it, and reports it exactly the way the internal
 * application does. A portal session DOES hold a hub connection — `app/providers.tsx` runs
 * `useRealtimeNotifications()` for every authenticated session, clients included, and the
 * portal's own `useClientChatRealtime` / `useClientNotificationsRealtime` subscribe to that
 * same `/hubs/notifications` singleton (the backend admits the external audience to the hubs
 * deliberately). Until 26.08.2026 the portal showed nothing, because the thing on offer was
 * `ConnectionStatusBanner` — a full-width orange strip IN THE FLOW that shoved every page down
 * ~30 px to announce a reconnect a customer cannot influence. Owner decision of that date:
 * the strip is gone from both surfaces, replaced by `ConnectionThread` — 2 px on the bottom
 * border of whichever bar is on screen, silent, and outside the layout entirely. The bars that
 * host it are `ClientHeader` and, on a phone, the three that render instead of it
 * (`ClientScreenAppBar`, `ClientSectionAppBar`).
 *
 * The offline banner below stays and is a different thing: `navigator.onLine`, the state a
 * customer can act on, said in words.
 *
 * `useClientSessionRealtime()` is mounted HERE rather than in a page hook (release Р4,
 * company-switch contract §2 remark 7): it is the only render site every authenticated
 * portal route shares, and `CompanySwitched` has to reach a tab sitting on
 * `/client/documents` just as much as one on `/client/messenger`.
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { ClientViewportProvider } from './client-nav/ClientViewportProvider';
import { useClientViewport } from './client-nav/useClientViewport';
import { useClientBack } from './client-nav/useClientBack';
import { useClientScrollRestoration } from './client-nav/useClientScrollRestoration';
import { useEdgeSwipeBack } from '@/hooks/useEdgeSwipeBack';
import { useVirtualKeyboardOpen } from '@/hooks/useVirtualKeyboardOpen';
import { useCurrentUser } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { unlockOrientation } from '@/lib/pwa';
import { useClientSessionRealtime } from '@/hooks/api/clientPortal/useClientSessionRealtime';
import { ClientHeader } from './ClientHeader';
import { ClientSidebar } from './ClientSidebar';
import { ClientBottomNav } from './ClientBottomNav';
import { ClientCreateFab } from './client-nav/ClientCreateFab';
import {
  ClientInstallPrompt,
  ClientOfflineBanner,
  ClientUpdateBanner,
} from './client-nav/ClientPwaBanners';
import { applyThemeColor, readThemeColor, resetThemeColor } from '@/theme/themeColor';
import {
  clientPhoneStatusBarSurface,
  isFullScreenPhonePath,
  isOwnAppBarPhonePath,
  isViewportLockedPath,
  matchClientRoute,
  usesGroupedPhoneCanvas,
  clientFabOwner,
} from './client-nav/clientRouteRegistry';

// ============================================================================
// Constants
// ============================================================================

/** localStorage key of the desktop collapse preference. Separate from the internal one. */
const CLIENT_SIDEBAR_COLLAPSED_KEY = 'client-sidebar-collapsed';

// ============================================================================
// Types
// ============================================================================

/**
 * Content only.
 *
 * The layout deliberately has NO `pageTitle` / `headerActions` / `contentClassName` props:
 * its single render site is `ClientProtectedLayout` (router/layouts.tsx), which renders
 * `<ClientLayout />` bare with the page underneath as an `<Outlet />`. A page can never
 * reach these props, so a prop that promised to set the header title was a slot that
 * merely looked wired. `ClientHeader` derives its title itself — see the fallback chain
 * there.
 */
export interface ClientLayoutProps {
  /** Page content */
  children: ReactNode;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Visually-hidden link that appears on keyboard focus and jumps past the navigation
 * straight to `<main id="main-content">` (WCAG 2.4.1). Same pattern as `AppLayout`.
 */
function SkipLink(): JSX.Element {
  return (
    <a
      href="#main-content"
      className="
        sr-only focus:not-sr-only
        focus:fixed focus:top-3 focus:left-3 focus:z-[10000]
        focus:px-4 focus:py-2 focus:rounded-md text-md
        focus:bg-primary-500 focus:text-[var(--color-text-on-primary)] focus:shadow-[var(--shadow-overlay)]
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      "
    >
      Перейти к основному содержимому
    </a>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Switch the portrait blocker off while the portal is on screen, and put it back exactly
 * as it was afterwards.
 *
 * «Поверните устройство» is a deliberate choice for a mechanic holding a phone, and
 * hostile for a customer with an iPad in landscape — 1024px lands exactly on the
 * blocker's media query. Two traps here, both worth stating:
 *
 *   1. **The flag is inverted.** `disableLandscapeOnMobile` defaults to `true`, and
 *      `App.tsx` computes `disabled = (value === false)`. So the blocker is switched OFF
 *      by writing `false`.
 *   2. **The store is persisted** to localStorage under `toss-ui-store`, i.e. per
 *      browser, not per session. Failing to restore the previous value would silently
 *      kill the portrait blocker for the next INTERNAL user of the same browser — which
 *      is why `pagehide` restores it too: a closed tab never unmounts anything.
 *      (`pageshow` re-applies after a bfcache restore, where the effect does not re-run.)
 *
 * `unlockOrientation()` releases the boot-time `screen.orientation.lock('portrait')`,
 * which `main.tsx` skips only for a session already known to be a client one.
 */
function usePortraitBlockerDisabled(): void {
  useEffect(() => {
    const previous = useUIStore.getState().disableLandscapeOnMobile;
    const { setDisableLandscapeOnMobile } = useUIStore.getState();
    const disable = (): void => setDisableLandscapeOnMobile(false);
    const restore = (): void => setDisableLandscapeOnMobile(previous);

    disable();
    unlockOrientation();

    window.addEventListener('pagehide', restore);
    window.addEventListener('pageshow', disable);
    return () => {
      window.removeEventListener('pagehide', restore);
      window.removeEventListener('pageshow', disable);
      restore();
    };
  }, []);
}

/**
 * Держит системную полосу над вебвью в цвете того, что под ней.
 *
 * Полоса — это статус-бар установленного кабинета и адресная строка браузера на телефоне; ни то
 * ни другое не элемент страницы, поэтому цвет объявляется тегом `<meta name="theme-color">`, а не
 * CSS. Правило владельца (01.09.2026): «или белый, или серый в зависимости от страницы — на
 * главной белый, так как там сверху панель, а страница чата и профиль серый должен быть». Какой
 * экран что носит, отвечает `clientPhoneStatusBarSurface` — та же таблица маршрутов, что раздаёт
 * остальной хром.
 *
 * Ставится ЗДЕСЬ, а не в шапках: тег в документе один, а шапок на телефоне три, и в переходе
 * между экранами две из них живут одновременно — они подрались бы за один тег. Слой же знает
 * адрес и меняется ровно один раз на переход.
 *
 * Не спрашивает ширину: `theme-color` на компьютере не виден вовсе (десктопные браузеры им
 * шапку не красят), а ветка по ширине здесь означала бы, что полоса зависит от размера окна —
 * то есть мигает при повороте планшета.
 *
 * На размонтировании возвращает значение из разметки: внутреннее приложение живёт в том же
 * документе, и своей брендовой полосы терять не должно.
 */
function useClientThemeColor(pathname: string): void {
  const initial = useRef<string | null>(null);
  if (initial.current === null) initial.current = readThemeColor();

  useEffect(() => {
    applyThemeColor(clientPhoneStatusBarSurface(pathname));
  }, [pathname]);

  useEffect(
    () => () => {
      if (initial.current !== null) resetThemeColor(initial.current);
    },
    []
  );
}

// ============================================================================
// Component
// ============================================================================

function ClientLayoutInner({ children }: ClientLayoutProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { showMobileNav, isTablet, isDesktop } = useClientViewport();
  // Names kept from the previous hook so the three render branches below read unchanged.
  const showSidebarCollapsed = isTablet;
  const showSidebarFull = isDesktop;

  /**
   * The scroll container on tablet and desktop. The root there is `h-dvh overflow-hidden`,
   * so `window.scrollTo()` has nothing to scroll — `<main>` is what moves. On the phone the
   * root is `min-h-dvh` and the DOCUMENT scrolls instead, which is why the reset below
   * touches both: whichever one is not the scroller is already at 0 and the write is a no-op.
   */
  const mainRef = useRef<HTMLElement>(null);

  usePortraitBlockerDisabled();
  useClientThemeColor(location.pathname);
  useClientSessionRealtime();

  /**
   * Remount key for the whole portal content.
   *
   * Clearing the query cache on a company switch takes care of everything that IS a query.
   * It does nothing for what pages hold in React state — the search box's text, the open
   * filter sheet, «показывать отменённые», a half-typed form — and those belong to the
   * company that was active when they were typed. Without this key the customer switches
   * legal entity, the list refetches under the new company, and the search field still
   * carries a container number from the old one, quietly filtering the new list to nothing.
   *
   * Changing the key unmounts the current screen and mounts it fresh, which resets exactly
   * that state and nothing else: the route, the scroll position and the chrome around this
   * element all stay. `authStore` is updated inside `applyCompanySwitch`, so this fires on
   * both switch paths — the dropdown here and the `CompanySwitched` push in another tab.
   */
  const activeCompanyId = useCurrentUser()?.customerId ?? 'no-company';


  /**
   * The floating button in the bottom-right corner, phone only.
   *
   * Shown where the screen's own action is a plausible next move — the landing screen and the
   * applications list («новая заявка»), «Сотрудники» («добавить пользователя») — and NOT where it
   * would be wrong or in the way:
   *
   *  - the create form itself: a button that opens the screen you are already on;
   *  - the messenger: it would sit on top of the message input;
   *  - documents, fleet, notifications, profile: reading screens, where a permanent circle
   *    over the content is decoration with a tap target.
   *
   * An allow-list rather than a deny-list on purpose — a screen added later gets no floating
   * button until someone decides it should have one.
   *
   * **Кто рисует — тоже вопрос таблицы.** `'application'` слой рисует сам: адрес один и тот же
   * везде. `'page'` он только резервирует — кнопка «Сотрудников» открывает форму, живущую в
   * адресе объектом (`useClientOverlaySubject`), и ссылка без объекта закрылась бы сама. Резерв
   * места одинаков в обоих случаях: пустой хвост в конце списка лучше, чем последняя строка,
   * припаркованная под 56-пиксельным кругом.
   */
  // Which chrome this route gets — `clientRouteRegistry` holds the table and the reasons.
  // Route and width are separate questions: those answer "what kind of screen this is", the
  // viewport answers "on what", and only the phone has kinds that swallow the chrome.
  const isFullScreenPhoneRoute = showMobileNav && isFullScreenPhonePath(location.pathname);
  const isOwnAppBarPhoneRoute = showMobileNav && isOwnAppBarPhonePath(location.pathname);

  /**
   * Third way back off a full-screen screen, beside its own arrow and the device's back
   * control: a swipe from the left edge. In a browser tab the OS provides this itself; in an
   * installed PWA it does not, and there the arrow would otherwise be the only exit.
   *
   * Literally the same call the arrow makes. It used to be a copy that fell back to the
   * dashboard while the arrow fell back to a per-screen prop — on «Шаблоны» the arrow led to
   * «Профиль» and the swipe to «Главная», which is two answers to one gesture.
   */
  const { goBack } = useClientBack();

  useEdgeSwipeBack({ enabled: isFullScreenPhoneRoute, onBack: goBack });

  const fabOwner = clientFabOwner(location.pathname);
  const showCreateFab = fabOwner !== null;

  /**
   * Phone-only screens, sent back to their parent at ≥768.
   *
   * «Личные данные», «Предприятие», «Сотрудники»(read-only), «Смена пароля» and the template form
   * exist as SCREENS on a phone and as cards or dialogs on the parent screen on a computer — a
   * form of eleven inputs under a touch keyboard is not a dialog, and a phone layout stretched
   * across 1280 px is nobody's idea of an improvement. Each of the five used to carry its own
   * copy of this redirect; the registry knows which routes they are, so one place is enough.
   *
   * `replace`, deliberately: a push would leave the phone-only address behind the parent, and the
   * back button would walk into a screen that immediately redirects forward again — two entries
   * that bounce off each other.
   */
  const phoneOnlyNode = matchClientRoute(location.pathname);
  const redirectFromPhoneOnly =
    !showMobileNav && phoneOnlyNode?.phoneOnly === true ? phoneOnlyNode.parent : null;

  // Desktop collapse preference (tablet is always collapsed and has no toggle).
  const [manualCollapse, setManualCollapse] = useState(
    () => localStorage.getItem(CLIENT_SIDEBAR_COLLAPSED_KEY) === 'true'
  );

  const handleToggleSidebar = (): void => {
    setManualCollapse((prev) => {
      const next = !prev;
      localStorage.setItem(CLIENT_SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  // Viewport-locked pages scroll inside themselves instead of scrolling the page: the
  // chat has to keep its composer pinned to the bottom. `AppLayout` takes this as a
  // `fullHeight` prop from `MessengerLayout`; the portal has a single route table that
  // later steps never touch, so the layout derives it from the path.
  const isViewportLocked = isViewportLockedPath(location.pathname);
  /**
   * Открыта ли экранная клавиатура. Нужна ровно в одном месте — резерв под нижнюю панель ниже:
   * панель под клавиатурой уезжает сама (`ClientBottomNav`), а её место в потоке обязано уехать
   * вместе с ней.
   */
  const isKeyboardOpen = useVirtualKeyboardOpen();
  /**
   * Class of the remount wrapper above.
   *
   * A plain `<div>` here silently broke the ONE screen that sizes itself against the
   * viewport instead of growing with its content. The chat is `h-full` inside `<main>`; the
   * wrapper sat between them with `height: auto`, so `h-full` resolved against nothing, the
   * chat grew to its content instead (944 px inside a 684 px `<main>` with
   * `overflow: hidden`) and the message composer was rendered BELOW the visible area — on
   * every width. A customer could read the chat and had no way to write in it.
   *
   * Only the viewport-locked route needs the height passed through; a scrolling screen must
   * NOT get `h-full`, which would clamp a long list to one screen and cut it off.
   */
  const remountWrapperClass = isViewportLocked ? 'h-full min-h-0 flex flex-col' : undefined;

  /**
   * Where a screen starts.
   *
   * A new place starts at the top; a screen returned to by «назад» starts where it was left —
   * otherwise the arrow gives back the list and takes away the forty cards the customer had
   * scrolled through. Both halves live in one hook, because they are one decision.
   *
   * Off on the chat, which pins itself to the newest message and would fight it.
   */
  useClientScrollRestoration(mainRef, { enabled: !isViewportLocked });

  /**
   * Канва профиля и всего, что за ним, на телефоне — серое поле мобильного приложения TOSS.
   *
   * Красится `<main>`, а не обёртка внутри страницы: серое должно доходить до низа экрана и при
   * коротком содержимом, а страница знает только высоту своего содержимого. Признак берётся из
   * той же таблицы маршрутов, что и остальной хром, — иначе экран, добавленный в профиль позже,
   * молча остался бы на общей канве.
   */
  const useGroupedCanvas = showMobileNav && usesGroupedPhoneCanvas(location.pathname);

  const mainClasses = [
    // `relative` — the scroller is also the containing block (25.09.2026, see `AppLayout`). An
    // `absolute` with no positioned ancestor of its own (the hidden `<select class="sr-only">` of
    // every `Select`) otherwise resolved against the document: it stayed at its place deep in the
    // page but OUTSIDE this scroll, the document grew taller than the `h-dvh` root, and the wheel
    // at the end of `<main>` carried the whole shell up and off the screen.
    'relative flex-1 min-h-0 w-full max-w-full focus:outline-none',
    isViewportLocked
      ? // A viewport-locked screen still stands ON the canvas, not on top of it: the same
        // horizontal field as every other page, and the rail's own gap top and bottom rather than
        // `page-padding-y`, because a conversation is measured in the viewport and 24 px twice
        // over is a message and a half. `AppLayout` says exactly this for the internal messenger.
        //
        // On a phone the route owns the whole viewport (`fixed inset-0` below), and 12 px of
        // margin around a conversation is a line of text traded for a decoration nobody asked for.
        showMobileNav
        ? 'overflow-hidden'
        : 'overflow-hidden page-padding py-[var(--sidebar-gap)]'
      : 'overflow-y-auto overflow-x-hidden page-padding page-padding-y',
    useGroupedCanvas ? 'bg-[var(--client-canvas)]' : '',
    // Атрибут ниже — то, чем канва переключает вид карточек (`.client-card` в `index.css`).
    // Класс фона и атрибут ставятся вместе и всегда вместе: серое поле без карточек приложения
    // и карточки приложения на светлой канве одинаково выглядят наполовину сделанными.
    // Room for the fixed bottom bar (64px + safe area) on the phone — and for the floating
    // «новая заявка» button above it where that is mounted, so the last row of a screen is
    // not parked under a 56 px circle the moment the list ends.
    !isViewportLocked && showMobileNav ? (showCreateFab ? '!pb-36' : '!pb-24') : '',
  ]
    .filter(Boolean)
    .join(' ');

  // --------------------------------
  // Phone (<= 767): header + content + fixed bottom navigation. No drawer.
  // --------------------------------
  // --------------------------------
  // Phone, full-screen route: the page owns the whole viewport and draws its own app bar.
  // --------------------------------
  if (redirectFromPhoneOnly) {
    return <Navigate to={redirectFromPhoneOnly} replace />;
  }

  if (isFullScreenPhoneRoute) {
    return (
      // Фон корня — та же канва, что у `<main>`, когда экран её просит: шапка на этом маршруте
      // скруглена снизу, и её углы должны вырезаться в серое поле. На светлой канве портала
      // скругления попросту не видно.
      <div
        className={`min-h-dvh overscroll-none client-safe-x flex flex-col max-w-full ${
          useGroupedCanvas ? 'bg-[var(--client-canvas)]' : 'bg-[var(--color-bg-app)]'
        }`}
      >
        <SkipLink />

        {/* Height of `ClientScreenAppBar`, which is `fixed` for the same body-overflow
            reason `ClientHeader` is. 56 px here, not 64: this bar carries one line. */}
        <div
          className="h-[calc(56px+env(safe-area-inset-top,0px))] flex-shrink-0"
          aria-hidden="true"
        />

        <ClientOfflineBanner />
        {/* Update only: an install card over «Новая заявка» or the chat interrupts a task. */}
        <ClientUpdateBanner />

        {/* Своя строка классов, а не `mainClasses`: у полноэкранного маршрута нет нижней панели,
            и резерв под неё (`!pb-24`) оставил бы внизу пустую ленту. Резерв под ПЛАВАЮЩУЮ кнопку
            здесь всё же нужен, когда она есть («Шаблоны»): без него последняя карточка списка
            паркуется под 56-пиксельным кругом. Канва и её атрибут — те же, что и у секционной
            ветки: экран, помеченный `phoneCanvas`, обязан выглядеть одинаково, каким бы из двух
            `<main>` его ни отрисовали. `relative` — как у `mainClasses`: скроллер и содержащий
            блок — один элемент. */}
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className={`relative flex-1 min-h-0 w-full max-w-full focus:outline-none overflow-y-auto overflow-x-hidden page-padding page-padding-y ${
            showCreateFab ? '!pb-24' : ''
          } ${useGroupedCanvas ? 'bg-[var(--client-canvas)]' : ''}`}
          data-client-canvas={useGroupedCanvas ? 'grouped' : undefined}
        >
          <div key={activeCompanyId} className={remountWrapperClass}>
            {children}
          </div>
        </main>
      </div>
    );
  }

  if (showMobileNav) {
    // `overscroll-none` — на ОБЕИХ ветках, а не только на залоченной (01.09.2026). Оттяжка
    // резинового скролла на телефоне обнажает холст документа, а `fixed`-шапка при этом
    // визуально отклеивается от содержимого. Внутреннее приложение добивается того же
    // императивно (`AppLayout`), кабинету хватает класса: корень здесь всегда один.
    //
    // No `overflow-x-hidden` on the wrapper while the page scrolls, and that is
    // load-bearing: ANY non-visible `overflow` makes an element a scroll container, and a
    // `sticky` descendant then sticks to IT rather than to the viewport. This wrapper does
    // not scroll — the document does — so the app bar simply scrolled away with the page.
    // The horizontal clamp lives on `<main>` (`overflow-x-hidden` in `mainClasses`), which
    // is where the content that could overflow actually is. The viewport-locked branch
    // keeps `overflow-hidden`: nothing scrolls there at all, so nothing can stick.
    return (
      <div
        className={`${
          isViewportLocked
            ? 'fixed inset-0 overflow-hidden'
            : 'min-h-dvh'
        } overscroll-none client-safe-x bg-[var(--color-bg-app)] flex flex-col max-w-full`}
      >
        <SkipLink />

        {/* A section screen brings its own bar AND its own spacer — see
            `isOwnAppBarPhoneRoute`. Rendering this one too would stack two headers. */}
        {!isOwnAppBarPhoneRoute && (
          <>
            <ClientHeader />

            {/* The app bar is `fixed` on the phone (see `ClientHeader` for why it cannot be
                `sticky` here), so its height has to be given back to the flow — otherwise
                the first card of every screen starts underneath it. */}
            <div
              className="h-[calc(64px+env(safe-area-inset-top,0px))] flex-shrink-0"
              aria-hidden="true"
            />
          </>
        )}

        {/* Not on a route whose PAGE draws the bar: there the flow starts under a `fixed`
            header, and both strips rendered here were invisible (top 0, height 35, the bar's
            `<h1>` returned by `elementFromPoint` at their centre — measured 24.08.2026).
            `ClientSectionAppBar` renders them inside itself instead, where its own spacer
            gives their height back to the content. */}
        {!isOwnAppBarPhoneRoute && (
          <>
            <ClientOfflineBanner />
            <ClientUpdateBanner />
          </>
        )}

        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className={mainClasses}
          data-client-canvas={useGroupedCanvas ? 'grouped' : undefined}
        >
          <div key={activeCompanyId} className={remountWrapperClass}>
            {children}
          </div>
        </main>

        {/* The bar is `fixed`, so a viewport-locked page needs the space reserved for
            it explicitly — the scrolling variant gets it from `!pb-24` instead.

            И отдаёт это место обратно, когда панель уезжает под клавиатуру: единственный
            экран в этой ветке — чат, и без этого его поле ввода оставалось бы висеть над пустой
            лентой в 64px ровно тогда, когда в него печатают. Длительность та же, что у панели. */}
        {isViewportLocked && (
          <div
            className={`flex-shrink-0 transition-[height] duration-200 ease-out motion-reduce:transition-none ${
              isKeyboardOpen ? 'h-0' : 'h-[calc(64px+env(safe-area-inset-bottom,0px))]'
            }`}
            aria-hidden="true"
          />
        )}
        {fabOwner === 'application' && <ClientCreateFab />}
        <ClientBottomNav />

        {/* Over the content, never inside it — see `ClientInstallPrompt` for the 86 px it used to
            take out of every screen. Off the chat, whose composer owns the bottom of the window. */}
        {!isViewportLocked && <ClientInstallPrompt layout="phone" />}
      </div>
    );
  }

  // --------------------------------
  // Tablet (768–1023, icon sidebar) and desktop (>= 1024, full sidebar)
  // --------------------------------
  return (
    <div className="h-dvh overflow-hidden overscroll-none client-safe-x bg-[var(--color-bg-app)] flex">
      <SkipLink />
      <ClientSidebar
        collapsed={showSidebarCollapsed || (showSidebarFull && manualCollapse)}
        onToggleCollapsed={showSidebarFull ? handleToggleSidebar : undefined}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Back from 768 up on 2026-08-24 (owner decision, reversing Р33 in favour of the
            wireframes, which draw this bar on every computer screen).

            Р33 removed it for a real defect: it printed the section name and the page printed the
            same name again — «Заявки» over «Заявки» on seven screens out of nine. That defect is
            fixed at the OTHER end this time. `ClientPageHeader` lost its `title` prop and became a
            toolbar, so no page prints a heading any more and the name appears exactly once. If a
            screen starts drawing its own title again, Р33 is back with it.

            С 27.08.2026 бар здесь — карточка на канве, как боковое меню и как содержимое.
            Отступы даёт эта обёртка, а не сам бар: `page-padding` — тот же класс, что у
            `<main>`, поэтому шапка и содержимое стоят на одной вертикали, а `--sidebar-gap`
            сверху — то же поле, что у карточки меню до края окна. Тулбар экрана остаётся под
            шапкой: перенос его внутрь собирали и смотрели, владелец выбрал оставить как есть. */}
        <div className="flex-shrink-0 page-padding pt-[var(--sidebar-gap)]">
          <ClientHeader variant="card" />
        </div>

        <ClientOfflineBanner />
        <ClientUpdateBanner />

        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className={mainClasses}
          data-client-canvas={useGroupedCanvas ? 'grouped' : undefined}
        >
          {/* A ceiling on a wide monitor. Without it a 1920 window gave the content 1672 px, and
              five columns of a registry drifted apart into a row of unrelated words. The chat is
              excluded: it sizes itself against the viewport and its side column is what fills the
              extra width. */}
          {/* `min-h-full flex flex-col` — а НЕ `h-full` (27.08.2026).
              `h-full` фиксировал высоту этой коробки по окну, и содержимое, которое выше, просто
              вываливалось за её край: поток `<main>` продолжал считать, что контента ровно на один
              экран. Отсюда два следствия, которые владелец видел глазами, — нижний `padding`
              `<main>` в конце прокрутки не отдавался (последняя карточка вплотную к кромке окна),
              и любой спейсер, дописанный в конец, попадал под этот же вываленный контент.
              `min-h-full` растёт вместе с содержимым и даёт тот же пол в один экран, когда его
              мало.

              А высоту окна страница просит через `client-content-box` (31.08.2026). Считалось, что
              `PageContainer fill` возьмёт её сам — «объявляет `flex-1 min-h-0`, коробка flex, значит
              список с `fillHeight` получает то же, что получал». Не получал: в flex-колонке с
              АВТОвысотой `flex: 1 1 0` растягивается по контенту, а `h-full` против `min-height`
              не резолвится вовсе. Реестр на 4000px делал эту коробку высотой 4086 вместо 552 —
              таблица переставала скроллиться внутри себя, скроллила вся страница, а заодно
              вырождались виртуализация и подгрузка по скроллу (их sentinel и мерка — контейнер
              таблицы, переставший быть скроллером).

              Обе высоты нужны одновременно, но разным страницам, поэтому переключает их
              `.client-content-box:has(> [data-page-fill])` в `index.css`: `height: 100%` ровно
              тогда, когда страница положила внутрь `PageContainer fill`. Признак ДИНАМИЧЕСКИЙ —
              «Заявки» включают `fill` только в виде «Таблица», — так что по таблице маршрутов, как
              остальной хром, его не вычислить. Условие правила требует, чтобы `PageContainer`
              оставался прямым ребёнком этой коробки: обёртка, дописанная между ними в роутере,
              молча вернёт всё к скроллу страницей. На это есть тест. */}
          <div
            key={activeCompanyId}
            className={[
              remountWrapperClass,
              isViewportLocked
                ? ''
                : 'client-content-box w-full max-w-[1440px] mx-auto min-h-full flex flex-col',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </div>
        </main>
      </div>

      {/* Same card as the phone's, in the corner instead of across the width. `fixed`, so the
          window's `h-dvh overflow-hidden` root keeps every pixel of its height for the content. */}
      {!isViewportLocked && <ClientInstallPrompt layout="desktop" />}
    </div>
  );
}

/**
 * The layout, plus the one viewport measurement everything under it reads.
 *
 * The provider wraps rather than sits inside: pages are rendered as `children` here, and they ask
 * for the viewport too. Without the wrapper each of them would fall back to measuring for itself —
 * correct, but back to a listener per component, which is what this replaced.
 */
export function ClientLayout({ children }: ClientLayoutProps): JSX.Element {
  return (
    <ClientViewportProvider>
      <ClientLayoutInner>{children}</ClientLayoutInner>
    </ClientViewportProvider>
  );
}

export default ClientLayout;
